import Chip from '@carvana/ds-chip';
import { SlateLogo } from '@carvana/ds-icons';
import cx from 'classnames';
import { Menu, ShoppingCart } from 'lucide-react';
import React, { useContext } from 'react';
import { Summary } from '|/app/services';
import { SLATE_HOME_URL } from '|/context';
import MobileNavigationRailContext from '|/context/mobile-navigation-rail-context';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import CarvanaWordmark from './carvana-wordmark';
import PoweredByCarvana from './powered-by-carvana';
import SlateBrandLockup from './slate-brand-lockup';
import SlateLogoDesktop from './slate-logo-desktop';
import styles from './slate-header.module.css';
import ImpersonationBanner from '../impersonation-baner';

interface SlateHeaderProps {
  orderSummary?: Summary | null;
  isOrderSummaryModalOpen?: boolean;
  setIsOrderSummaryModalOpen?: (isOpen: boolean) => void;
  inlineBrand?: boolean;
  showMobileMenu?: boolean;
}

const SlateHeader: React.FC<SlateHeaderProps> = ({
  orderSummary = null,
  isOrderSummaryModalOpen = false,
  setIsOrderSummaryModalOpen,
  inlineBrand = false,
  showMobileMenu = false
}) => {
  const { trackEvent } = useAnalyticsContext();
  const { setShowMobileNavigationRail } = useContext(MobileNavigationRailContext);

  const onLogoClick = () => {
    trackEvent('pp_clicked_logo_icon');
  };

  const onOrderSummaryClick = () => {
    trackEvent('pp_header_cart_clicked');
    setIsOrderSummaryModalOpen?.(true);
  };

  const showNavigationMenu = () => {
    trackEvent('pp_clicked_list_icon');
    setShowMobileNavigationRail(value => !value);
  };

  const showOrderSummary = Boolean(setIsOrderSummaryModalOpen && orderSummary?.vehicle);

  return (
    <>
      <ImpersonationBanner />
      <header className={styles.header}>
        <div className={cx(styles.brand, inlineBrand && styles.brandInline)}>
          <a
            href={SLATE_HOME_URL}
            className={styles.logoLink}
            onClick={onLogoClick}
            data-analytics-id="pp_header_home"
            aria-label="Slate Home"
          >
            <SlateBrandLockup className={styles.brandLockup} />
            <SlateLogoDesktop className={styles.slateLogoDesktop} />
            <SlateLogo className={styles.slateLogo} color="currentColor" />
          </a>
          <PoweredByCarvana className={styles.poweredByCarvana} />
          <span className={styles.poweredBy}>
            <span className={styles.poweredByText}>Powered by</span>
            <CarvanaWordmark className={styles.carvanaWordmark} />
          </span>
        </div>

        <div className={styles.actions}>
          {showOrderSummary && (
            <Chip
              shape="rectangle"
              size="default"
              icon={<ShoppingCart size={16} color="currentColor" />}
              iconPosition="left"
              onClick={onOrderSummaryClick}
              selected={isOrderSummaryModalOpen}
              className={styles.orderSummaryChip}
              data-analytics-id="pp_header_cart"
            >
              Order Summary
            </Chip>
          )}

          {showMobileMenu && (
            <button
              className={styles.mobileMenuButton}
              onClick={showNavigationMenu}
              data-analytics-id="pp_header_mobile_list_icon"
              aria-label="Open navigation menu"
            >
              <Menu size={24} />
            </button>
          )}
        </div>
      </header>
    </>
  );
};

export default SlateHeader;
